/**
 * ITBP Reports — tiny dashboard for completion % + pending-work + tech perf.
 */
import { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Loader2 } from 'lucide-react';

interface Summary {
  total: number;
  byStatus: { approved: number; submitted: number; partial: number; pending: number; inProgress: number; rejected: number };
  completionPct: number;
  pendingCount: number;
  byTechnician: Array<{ technicianEmployeeId: string | null; _count: number }>;
}

export function ITBPReportsPage() {
  const [s, setS] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/installation-jobs/reports/summary')
      .then(r => setS(r.data?.data ?? null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>;
  if (!s) return <div className="p-10 text-center text-gray-500">No data.</div>;

  const maxTech = Math.max(1, ...s.byTechnician.map(t => t._count));
  const statusEntries = Object.entries(s.byStatus) as Array<[keyof Summary['byStatus'], number]>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">ITBP Reports</h1>
      <p className="text-sm text-gray-500 mb-6">Installation verification performance — Ladakh deployment.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Total Jobs" value={s.total} />
        <Kpi label="Completion %" value={`${s.completionPct}%`} />
        <Kpi label="Pending Work" value={s.pendingCount} tone="bg-orange-50 text-orange-700" />
        <Kpi label="Rejected" value={s.byStatus.rejected} tone="bg-red-50 text-red-700" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Status Breakdown</div>
          {statusEntries.map(([k, v]) => {
            const pct = s.total > 0 ? (v / s.total) * 100 : 0;
            return (
              <div key={k} className="mb-2">
                <div className="flex justify-between text-xs mb-1"><span className="capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}</span><span>{v}</span></div>
                <div className="h-2 bg-gray-100 rounded">
                  <div className="h-2 bg-indigo-500 rounded" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Technician Performance (jobs handled)</div>
          {s.byTechnician.length === 0 ? (
            <div className="text-xs text-gray-400">No technicians assigned yet.</div>
          ) : s.byTechnician.map((t, i) => {
            const pct = (t._count / maxTech) * 100;
            return (
              <div key={t.technicianEmployeeId ?? i} className="mb-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-mono">{(t.technicianEmployeeId ?? '—').slice(-6)}</span>
                  <span>{t._count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded">
                  <div className="h-2 bg-emerald-500 rounded" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = 'bg-slate-50 text-slate-800' }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className={`rounded-lg p-4 ${tone}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
